import React, { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { auth as firebaseAuth } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { CellCogEvents } from '../../utils/analytics';
import Login from './Login';
import SignUp from './SignUp';

const AuthPage = ({ mode }) => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const electronRedirectDone = useRef(false);

  const isSignup = mode === 'signup' || location.pathname === '/signup';

  // Was the visitor ALREADY authenticated when they landed here? Captured once
  // on mount: mount-time authed users never see the form (instant redirect),
  // while a later currentUser flip is a fresh login/signup completing. This
  // closes the gap where /login pageviews far exceed login events — a chunk of
  // those visitors were never login attempts at all (see TRACKING.md).
  const wasLoggedInOnMount = useRef(null);
  if (wasLoggedInOnMount.current === null) {
    wasLoggedInOnMount.current = Boolean(currentUser);
  }

  useEffect(() => {
    if (wasLoggedInOnMount.current) {
      CellCogEvents.authAlreadyLoggedIn(isSignup ? 'signup' : 'login');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Check if this is an Electron app login flow
  const searchParams = new URLSearchParams(location.search);
  const isElectronLogin = searchParams.get('electron') === 'true';

  // Where to go after sign-in: the location ProtectedRoute saved, path AND query. Reading only the
  // pathname dropped "?tab=billing" / "?tab=notifications" from email deep links (Sep 10 2026), so a
  // signed-out click on "Update payment method" landed on the profile's first section, not Billing.
  const savedLocationUrl = (loc) => {
    const from = loc.state?.from;
    if (!from?.pathname) return '/';
    return `${from.pathname}${from.search || ''}${from.hash || ''}`;
  };

  useEffect(() => {
    if (currentUser) {
      // If this is an Electron login and user is already authenticated,
      // redirect to the callback with the Firebase token instead of going to /
      if (isElectronLogin && !electronRedirectDone.current) {
        electronRedirectDone.current = true;
        const user = firebaseAuth.currentUser;
        if (user) {
          user.getIdToken().then(token => {
            const email = user.email || '';
            const refreshToken = user.stsTokenManager?.refreshToken || '';
            window.location.href = `/auth/electron-callback?token=${encodeURIComponent(token)}&email=${encodeURIComponent(email)}&refreshToken=${encodeURIComponent(refreshToken)}`;
          }).catch(err => {
            console.error('Failed to get token for Electron:', err);
            navigate(savedLocationUrl(location), { replace: true });
          });
          return; // Don't navigate to / — wait for the token redirect
        }
      }

      navigate(savedLocationUrl(location), { replace: true });
    }
  }, [currentUser, navigate, location, isElectronLogin]);

  // Render based on mode prop or route
  if (isSignup) {
    return <SignUp />;
  }

  return <Login />;
};

export default AuthPage;
